/**
 * ErrorBoundary - port from `1116/omega-club/dashboard/src/components/ErrorBoundary.jsx`.
 * Class component (still the only way to do error boundaries in React 19).
 *
 * Wrap any subtree that might throw. Renders a friendly fallback with a
 * "Try again" button that resets the error state.
 *
 * Per CHAP-89 (Phase 1C #55).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. If omitted, the default "Something went wrong" panel renders. */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry/observability integration lands in CHAP-114 (Phase 1H #98).
    // For now, surface to console in dev so we don't lose the trace.
    if (typeof console !== 'undefined' && console.error) {
      console.error('[ErrorBoundary] caught:', error, info);
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.reset);
      }
      return (
        <div className="flex items-center justify-center h-64 min-w-0 p-4">
          <div className="text-center max-w-md">
            <p className="text-navy font-semibold">Something went wrong</p>
            {this.state.error?.message && (
              <p className="text-xs text-neutral mt-2 break-words">{this.state.error.message}</p>
            )}
            <button
              type="button"
              onClick={this.reset}
              className="mt-3 text-sm text-orange hover:underline cursor-pointer"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
